import React, { useEffect, useRef, useState } from "react";
import { Snackbar, Grow, Tooltip, CardActionArea } from "@mui/material";
import { SEO } from "../../components/common/SEO";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import {
  Grid,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  Link,
  Chip,
  Skeleton,
  Divider,
  CircularProgress,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ClockIcon from "@mui/icons-material/Schedule";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReturnIcon from "@mui/icons-material/Cached";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ShareIcon from "@mui/icons-material/Share";
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import SignUpSection from "../../components/common/SignUpCTA";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ReactRouterLink from "../../components/common/ReactRouterLink";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchProductById } from "../../redux/actions/productActions";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDaysToCurrentTimeStamp,
  createProductImageObject,
  deleteSession,
  formatDate,
  getCookie,
  getLoginCookieAndSession,
  getSellerName,
  getSession,
  isDesktop,
  isMobile,
  isTablet,
  sendConfirmationEmail,
} from "../../utils";
import { ProductStock } from "../../components/product/ProductStock";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import { FREE_SHIPPING_PRICE } from "../../redux/types/productTypes";
import { ImageGallery } from "../../components/common/ImageGallery";
import SnackbarAlert, {
  SnackbarRef,
} from "../../components/common/SnackbarAlert";
import { DrawerModal } from "../../components/common/DrawerModal";
import ComingSoon from "../../components/common/ComingSoon";
import ToyCard from "../../components/product/ToyCard";
import { SocialMediaShare } from "../../components/product/SocialMediaShare";
import { ProductSchema } from "../../components/product/ProductSchema";
import { fetchBlogById } from "../../redux/actions/blogActions";
import he from "he";
import { BASE_URL_WEB_APP, USER_JUST_LOGGED_IN } from "../../constants";
import Login from "../../components/common/Login";
import { fetchUserByUniqueId } from "../../redux/actions/userActions";
import useFetchUserDetails from "../../hooks/useFetchCurrentUserDetails";
import {
  createWishlist,
  fetchWishlistProductByUniqueId,
  removeWishlist,
} from "../../redux/actions/wishlistAction";
import { createCart } from "../../redux/actions/cartAction";
import { profile } from "console";
import { sendEmail } from "../../emailHandler";
import {
  createReportedProduct,
  fetchReportedProductByUniqueId,
} from "../../redux/actions/reportedProductsAction";

const ProductView: React.FC = () => {
  const { users } = useFetchUserDetails();

  const snackbarRef = useRef<SnackbarRef>(null);
  const isFetchingRef = useRef(false);
  const previousProductUrlRef = useRef<string>("");
  const [isMakeOfferModalOpen, setIsMakeOfferModalOpen] = useState(false);
  const [isAIAnalysisModalOpen, setIsAIAnalysisModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  // const [isProtectionBlogModalOpen, setIsProtectionBlogModalOpen] =
  //   useState(false);
  const [isBlogModalOpen, setIsBlogModalOpen] = useState(false);
  const [isAccountModalOpen, setAccountModalOpen] = React.useState(false);
  const [showSignInAlertMessage, setShowSignInAlertMessage] =
    React.useState(true);
  const [isAlertVerifyUserModalOpen, setIsAlertVerifyUserModalOpen] =
    React.useState(false);
  const [isAddingToCart, setIsAddingToCart] = React.useState(false);

  const { category, subCategory, name } = useParams();
  const navigate = useNavigate();

  const profileId = getLoginCookieAndSession();

  const dispatch = useDispatch();
  const { loading, productsData } = useSelector(
    (state: RootState) => state.products,
  );
  const { products, moreProducts, similarProducts, sellerInfo } = productsData;
  const { loading: blogsLoading, blogsData } = useSelector(
    (state: RootState) => state.blogs,
  );

  const {
    loading: wishlistLoading,
    wishlistData,
    error: wishlistError,
  } = useSelector((state: RootState) => state.wishlists);
  const { wishlists } = wishlistData;

  const {
    loading: reportedProductLoading,
    reportedProductsData,
    error: reportedProductError,
  } = useSelector((state: RootState) => state.reportedProducts);
  const { reportedProducts, success } = reportedProductsData;

  // Check if product is deleted (only when user explicitly deletes, not just out of stock)
  const isProductDeleted =
    products &&
    products.length > 0 &&
    (products[0].isDeleted ||
      !products[0].productOwner ||
      products[0].productOwner === "");

  // Check if product is out of stock (separate from deleted)
  const isOutOfStock =
    products && products.length > 0 && products[0].productQuantity === 0;

  useEffect(() => {
    if (category && subCategory && name) {
      const productURL = `${category}/${subCategory}/${name}`.toLowerCase();
      // Prevent duplicate fetches for the same product
      if (
        productURL !== previousProductUrlRef.current &&
        !isFetchingRef.current
      ) {
        previousProductUrlRef.current = productURL;
        isFetchingRef.current = true;
        dispatch(fetchProductById(`${productURL}`));
      }
    } else {
      navigate("/not-found");
    }
  }, [category, dispatch, name, navigate, subCategory]);

  // Reset fetching ref when loading completes
  useEffect(() => {
    if (!loading) {
      isFetchingRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    if (products && products.length > 0) {
      dispatch(
        fetchWishlistProductByUniqueId(
          Number(profileId),
          products[0].createdTime,
        ),
      );

      dispatch(
        fetchReportedProductByUniqueId(
          Number(profileId),
          products[0].createdTime,
        ),
      );
    }
  }, [products]);

  useEffect(() => {
    if (reportedProductError?.includes("hours")) {
      snackbarRef.current?.showSnackbar(reportedProductError, "warning");
    }
  }, [reportedProductError]);

  useEffect(() => {
    if (success) {
      snackbarRef.current?.showSnackbar("Report sent successfully", "success");
    }
  }, [success]);

  if (!loading && products && products.length <= 0) {
    navigate("/not-found");
    return null;
  }

  const handleReportClick = async () => {
    if (users && users.length) {
      if (users[0].isVerified) {
        const productURL = `${BASE_URL_WEB_APP}${products[0].productUrl}`;
        dispatch(
          createReportedProduct(
            Number(profileId),
            products[0].createdTime,
            productURL,
            users[0].name,
          ),
        );
      } else {
        setIsAlertVerifyUserModalOpen(true);
      }
    } else {
      setAccountModalOpen(true);
      setShowSignInAlertMessage(true);
    }
  };

  const handleWishlistClick = () => {
    if (profileId) {
      if (wishlists.length > 0) {
        // Remove from wishlist
        dispatch(removeWishlist(Number(profileId), products[0].createdTime));
        snackbarRef.current?.showSnackbar(
          "Removed from your wishlist",
          "success",
        );
      } else {
        // Check if the user is trying to add their own product
        if (
          users &&
          users.length > 0 &&
          users[0].email === products[0].productOwner
        ) {
          snackbarRef.current?.showSnackbar(
            "You can't add your own item",
            "warning",
          );
          return;
        }
        // Add to wishlist
        dispatch(createWishlist(Number(profileId), products[0].createdTime));
        snackbarRef.current?.showSnackbar("Saved to your wishlist", "success");
      }
    } else {
      setAccountModalOpen(true);
      setShowSignInAlertMessage(true);
    }
  };

  const handleAddToCart = async () => {
    if (profileId) {
      // If logged in and out of stock, show message instead of adding
      if (isOutOfStock) {
        snackbarRef.current?.showSnackbar(
          "This item is out of stock",
          "warning",
        );
        return;
      }

      // Check if the user is trying to add their own product
      if (
        users &&
        users.length > 0 &&
        users[0].email === products[0].productOwner
      ) {
        snackbarRef.current?.showSnackbar(
          "You can't add your own item",
          "warning",
        );
        return;
      }

      setIsAddingToCart(true);
      try {
        await dispatch(
          createCart(Number(profileId), products[0].createdTime, 1) as any,
        );
        snackbarRef.current?.showSnackbar("Added to your cart", "success");
      } catch (error: any) {
        snackbarRef.current?.showSnackbar(
          error?.message || "Failed to add item to cart",
          "error",
        );
      } finally {
        setIsAddingToCart(false);
      }
    } else {
      setAccountModalOpen(true);
      setShowSignInAlertMessage(true);
    }
  };

  const handleMakeOffer = () => {
    if (profileId) {
      // If logged in and out of stock, show message instead of opening modal
      if (isOutOfStock) {
        snackbarRef.current?.showSnackbar(
          "This item is out of stock",
          "warning",
        );
        return;
      }
      setIsMakeOfferModalOpen(true);
    } else {
      setAccountModalOpen(true);
      setShowSignInAlertMessage(true);
    }
  };
  const openMakeOfferModal = () => setIsMakeOfferModalOpen(true);
  const closeMakeOfferModal = () => setIsMakeOfferModalOpen(false);

  const handleAiAnalysis = () => {
    if (profileId) {
      setIsAIAnalysisModalOpen(true);
    } else {
      setAccountModalOpen(true);
      setShowSignInAlertMessage(true);
    }
  };
  const openAIAnalysisModal = () => setIsAIAnalysisModalOpen(true);
  const closeAIAnalysisModal = () => setIsAIAnalysisModalOpen(false);

  const openShareModal = () => setIsShareModalOpen(true);
  const closeShareModal = (closeAfterSuccess = false) => {
    setIsShareModalOpen(false);
    if (typeof closeAfterSuccess === "boolean" && closeAfterSuccess === true) {
      snackbarRef.current?.showSnackbar("Link copied", "success");
    }
  };
  const openBlogModal = (id?: string) => {
    dispatch(fetchBlogById(id || ""));
    setIsBlogModalOpen(true);
  };
  const closeBlogModal = () => setIsBlogModalOpen(false);

  const handleMailTo = () => {
    if (users && users.length) {
      if (users[0].isVerified) {
        const email = products[0].productOwner;
        const subject = "Question About Your listing on FiddlePiddle";
        const body = `I have a question about ${products[0].productName} - ${window.location.origin}${products[0].productUrl}\n\n`;

        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
          subject,
        )}&body=${encodeURIComponent(body)}`;

        window.open(mailtoLink, "_blank");
      } else {
        setIsAlertVerifyUserModalOpen(true);
      }
    } else {
      setAccountModalOpen(true);
      setShowSignInAlertMessage(true);
    }
  };

  const handleSendConfirmEmail = async () => {
    if (users && users.length) {
      await sendConfirmationEmail(users[0].email, users[0].createdTime);
      snackbarRef.current?.showSnackbar(
        "Confirmation email resent. Please check your inbox.",
        "success",
      );
      setIsAlertVerifyUserModalOpen(false);
    }
  };

  const ButtonStyled: React.FC<{
    text: string;
    variant?: "contained" | "outlined";
    color?: "primary" | "secondary";
    clickHandler?: () => void;
    disabled?: boolean;
    loading?: boolean;
  }> = ({
    text,
    variant = "contained",
    color = "primary",
    clickHandler,
    disabled = false,
    loading = false,
  }) => (
    <Button
      variant={variant}
      color={color}
      disabled={disabled || loading}
      sx={{
        borderRadius: "20px",
        width: "100%",
        marginBottom: "10px",
        color: variant === "contained" ? "white" : "primary",
        whiteSpace: "nowrap",
      }}
      onClick={clickHandler}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : text}
    </Button>
  );

  return loading || sellerInfo === undefined ? (
    <CenteredLoading />
  ) : (
    <>
      <SEO
        title={`${products[0].productName} • FiddlePiddle • Toys Marketplace`}
        description={products[0].productDescriprion}
        keywordsContent={`${products[0].productName}, ${products[0].productCategoryName}, ${products[0].productSubCategoryName}, buy sell toys, toys marketplace, marketplace for toys, fiddlepiddle`}
        ogImage={products[0].productImageOne}
      />
      <div style={{ position: "relative", zIndex: 100 }}>
        <Breadcrumb
          items={[
            { text: "Home", url: "/" },
            {
              text: products[0].productCategoryName,
              url: `/${products[0].productCategorySlug}`,
            },
            {
              text: products[0].productSubCategoryName,
              url: `/${products[0].productCategorySlug}/${products[0].productSubCategorySlug}`,
            },
            { text: products[0].productName, url: "" },
          ]}
        />
      </div>
      <Container maxWidth="xl" sx={{ paddingTop: 0 }}>
        <ProductSchema product={products[0]} />
        <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }}>
          <Grid item xs={12} md={8}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: isMobile ? "100vw" : "100%",
                minWidth: "100%",
                minHeight: isMobile ? "300px" : isTablet ? "450px" : "600px",
                marginBottom: "10px",
                backgroundColor: "#f5f5f5",
                borderRadius: isMobile ? "0" : "16px",
                overflow: "hidden",
                marginLeft: isMobile ? "-16px" : 0,
                position: "relative", // add position relative to the container
              }}
            >
              <ImageGallery
                images={createProductImageObject(
                  products[0].productImageOne,
                  products[0].productImageTwo,
                  products[0].productImageThree,
                  products[0].productName,
                )}
              />
            </div>
            {!isMobile ? (
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    mt: 2,
                    mb: 2,
                    border: "none",
                    borderRadius: {
                      xs: "22px",
                      md: "28px",
                    },
                    color: "#ffffff",
                    background:
                      "linear-gradient(135deg, #1b252d 0%, #293b46 58%, #3b5360 100%)",
                    boxShadow: "0 20px 48px rgba(28, 38, 46, 0.18)",

                    "&::before": {
                      content: '""',
                      position: "absolute",
                      width: {
                        xs: 260,
                        md: 400,
                      },
                      height: {
                        xs: 260,
                        md: 400,
                      },
                      top: {
                        xs: -150,
                        md: -230,
                      },
                      right: {
                        xs: -130,
                        md: -110,
                      },
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle, rgba(231,135,88,0.7) 0%, rgba(231,135,88,0) 70%)",
                      pointerEvents: "none",
                    },

                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      opacity: 0.15,
                      backgroundImage: `
          linear-gradient(
            rgba(255,255,255,0.08) 1px,
            transparent 1px
          ),
          linear-gradient(
            90deg,
            rgba(255,255,255,0.08) 1px,
            transparent 1px
          )
        `,
                      backgroundSize: "44px 44px",
                      maskImage:
                        "linear-gradient(to right, rgba(0,0,0,0.8), transparent)",
                      pointerEvents: "none",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      p: {
                        xs: 2.5,
                        sm: 3.5,
                        md: 4,
                      },

                      "&:last-child": {
                        pb: {
                          xs: 2.5,
                          sm: 3.5,
                          md: 4,
                        },
                      },
                    }}
                  >
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={7}>
                        <Typography
                          variant="overline"
                          sx={{
                            display: "block",
                            mb: 0.75,
                            color: "#ffad82",
                            fontWeight: 850,
                            letterSpacing: "0.09em",
                          }}
                        >
                          FIDDLEPIDDLE ON THE GO
                        </Typography>

                        <Typography
                          component="h3"
                          sx={{
                            mb: 1,
                            maxWidth: 590,
                            fontSize: {
                              xs: "1.65rem",
                              sm: "2rem",
                              md: "2.25rem",
                            },
                            lineHeight: 1.12,
                            fontWeight: 900,
                            letterSpacing: "-0.035em",
                          }}
                        >
                          Buy and sell toys wherever you are.
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 590,
                            color: "rgba(255,255,255,0.7)",
                            fontSize: {
                              xs: "0.92rem",
                              sm: "1rem",
                            },
                            lineHeight: 1.65,
                          }}
                        >
                          Browse new listings, manage your sales, follow
                          purchases, and keep up with your FiddlePiddle account
                          right from your phone.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6} md={12}>
                            <Button
                              component="a"
                              href="https://play.google.com/store/apps/details?id=com.alaaclips.fiddlepiddle&pli=1"
                              target="_blank"
                              rel="noopener noreferrer"
                              fullWidth
                              variant="contained"
                              startIcon={<AndroidIcon />}
                              sx={{
                                minHeight: 54,
                                px: 2.5,
                                borderRadius: "16px",
                                color: "#20252a",
                                bgcolor: "#ffffff",
                                fontSize: "0.95rem",
                                fontWeight: 850,
                                textTransform: "none",
                                boxShadow: "0 12px 26px rgba(0,0,0,0.18)",
                                transition:
                                  "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",

                                "& .MuiButton-startIcon": {
                                  mr: 1.25,
                                },

                                "&:hover": {
                                  color: "#20252a",
                                  bgcolor: "#f5f6f7",
                                  boxShadow: "0 16px 32px rgba(0,0,0,0.24)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              Get it on Google Play
                            </Button>
                          </Grid>

                          <Grid item xs={12} sm={6} md={12}>
                            <Button
                              component="a"
                              href="https://apps.apple.com/us/app/fiddlepiddle/id1597349309?platform=iphone"
                              target="_blank"
                              rel="noopener noreferrer"
                              fullWidth
                              variant="outlined"
                              startIcon={<AppleIcon />}
                              sx={{
                                minHeight: 54,
                                px: 2.5,
                                borderRadius: "16px",
                                color: "#ffffff",
                                borderColor: "rgba(255,255,255,0.35)",
                                bgcolor: "rgba(255,255,255,0.08)",
                                fontSize: "0.95rem",
                                fontWeight: 850,
                                textTransform: "none",
                                backdropFilter: "blur(10px)",
                                transition:
                                  "transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",

                                "& .MuiButton-startIcon": {
                                  mr: 1.25,
                                },

                                "&:hover": {
                                  color: "#ffffff",
                                  borderColor: "rgba(255,255,255,0.7)",
                                  bgcolor: "rgba(255,255,255,0.15)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              Download on the App Store
                            </Button>
                          </Grid>
                        </Grid>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1.5,
                            color: "rgba(255,255,255,0.52)",
                            textAlign: {
                              xs: "center",
                              md: "left",
                            },
                          }}
                        >
                          Free to download on Android and iPhone.
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ) : null}
            {!isTablet &&
            moreProducts &&
            moreProducts.length > 0 &&
            !isProductDeleted ? (
              <Grid item xs={12} sx={{ padding: 0, mt: 2 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <Typography variant="h6">
                    More listings from {getSellerName(products[0].productOwner)}
                  </Typography>
                  <ReactRouterLink
                    to={`/u/${sellerInfo.createdTime}/profile`}
                    isLinkUnderline={false}
                    linkElement={
                      <Chip
                        size="small"
                        label="See More"
                        icon={<VisibilityOutlinedIcon />}
                        onClick={() => {}}
                        sx={{ marginLeft: "5px" }}
                      />
                    }
                  />
                </span>

                <Grid container spacing={2}>
                  {moreProducts.map((product, index) => (
                    <Grid item key={index} xs={6} sm={4} md={3} lg={2}>
                      <ReactRouterLink
                        to={product.productUrl}
                        isLinkUnderline={false}
                        linkElement={
                          <CardActionArea
                            style={{
                              outline: "1px solid #f0f0f0",
                              borderRadius: "8px",
                            }}
                          >
                            <ToyCard product={product} loading={loading} />
                          </CardActionArea>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ) : null}
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              padding: 0,
              color: "secondary",
              marginTop: { xs: "-24px", md: "0px" },
            }}
          >
            <Card variant="outlined" sx={{ borderRadius: "16px" }}>
              <CardContent>
                <CardContent
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 0,
                  }}
                >
                  <Chip
                    size="small"
                    label="Report"
                    icon={<ReportGmailerrorredOutlinedIcon />}
                    onClick={handleReportClick}
                    sx={{ marginBottom: "10px" }}
                    disabled={
                      reportedProductLoading || reportedProducts.length > 0
                    }
                  />

                  <Chip
                    size="small"
                    label="Share"
                    icon={<ShareIcon />}
                    onClick={() => {
                      setIsShareModalOpen(true);
                    }}
                    sx={{
                      marginBottom: "10px",
                      ml: "auto",
                    }} // Align to the right margin
                  />

                  <Chip
                    size="small"
                    label={
                      wishlists.length > 0
                        ? "Remove from Wishlist"
                        : "Add to Wishlist"
                    }
                    icon={
                      wishlists.length > 0 ? (
                        <FavoriteIcon style={{ color: "#ff5c5f" }} />
                      ) : (
                        <FavoriteBorderOutlinedIcon
                          style={{ color: "#ff5c5f" }}
                        />
                      )
                    }
                    onClick={handleWishlistClick}
                    sx={{ marginLeft: "10px" }}
                    disabled={wishlistLoading || isProductDeleted}
                  />
                </CardContent>
                <Typography
                  variant="body2"
                  color="grey"
                  gutterBottom
                  style={{ marginTop: "8px" }}
                >
                  <Link
                    href={`/${products[0].productCategorySlug}`}
                    style={{ color: "grey", textDecoration: "none" }}
                  >
                    {products[0].productCategoryName}
                  </Link>{" "}
                  {" > "}
                  <Link
                    href={`/${products[0].productCategorySlug}/${products[0].productSubCategorySlug}`}
                    style={{ color: "grey", textDecoration: "none" }}
                  >
                    {products[0].productSubCategoryName}
                  </Link>
                </Typography>
                <Typography variant="h5">{products[0].productName}</Typography>
                <Typography variant="body2" color="grey">
                  Sold by{" "}
                  {isProductDeleted ? (
                    "Archived seller"
                  ) : (
                    <>
                      <ReactRouterLink
                        to={`/u/${sellerInfo.createdTime}/profile`}
                        linkElement={getSellerName(products[0].productOwner)}
                      />{" "}
                      <Tooltip
                        enterTouchDelay={0}
                        leaveTouchDelay={0}
                        title={
                          <React.Fragment>
                            <Typography color="inherit">
                              Verified Seller
                            </Typography>
                            This seller is verified.{" "}
                            <a
                              onClick={() =>
                                openBlogModal(
                                  "verified-sellers-fiddlepiddle-secure-transactions",
                                )
                              }
                              style={{
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              Learn more.
                            </a>
                          </React.Fragment>
                        }
                        arrow
                      >
                        <VerifiedOutlinedIcon
                          sx={{ color: "#2AAA8A", marginRight: "8px" }}
                        />
                      </Tooltip>
                    </>
                  )}
                  <Chip
                    size="small"
                    label="Contact Seller"
                    icon={<PersonOutlineOutlinedIcon />}
                    sx={{ marginLeft: "10px" }}
                    onClick={handleMailTo}
                    disabled={isProductDeleted}
                  />
                </Typography>
                <div
                  style={{
                    display: "flex",
                    marginTop: "20px",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h5" gutterBottom>
                    <b>{products[0].productPrice}</b>
                  </Typography>

                  <Button
                    variant="contained"
                    color="secondary"
                    style={{
                      borderRadius: "20px",
                      color: "#00ffde",
                      height: "100%",
                      lineHeight: "baseline",
                      position: "relative",
                      overflow: "hidden",
                      whiteSpace: "nowrap", // Prevent text wrapping
                      display: "flex", // Use flexbox for layout
                      alignItems: "center", // Center align items
                    }}
                    sx={{
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "0",
                        left: "-100%",
                        width: "200%",
                        height: "100%",
                        background:
                          "linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent)",
                        animation: "sheen-animation 2s linear infinite",
                      },
                      "&:hover::before": {
                        transform: "translateX(100%)",
                      },
                      "@keyframes sheen-animation": {
                        "0%": {
                          transform: "translateX(-100%)",
                        },
                        "100%": {
                          transform: "translateX(100%)",
                        },
                      },
                    }}
                    onClick={handleAiAnalysis}
                  >
                    <AutoAwesomeOutlinedIcon sx={{ mr: 1 }} />
                    AI Analysis
                  </Button>
                </div>
                <Typography variant="body1" gutterBottom>
                  Condition: <b>{products[0].productCondition}</b>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Availability:{" "}
                  {isProductDeleted ? (
                    <b>N/A</b>
                  ) : profileId !== null ? (
                    <ProductStock quantity={products[0].productQuantity} />
                  ) : (
                    <Link
                      onClick={() => {
                        setAccountModalOpen(true);
                        setShowSignInAlertMessage(false);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      Sign in to view availability
                    </Link>
                  )}
                </Typography>

                <Divider sx={{ mt: 2, mb: 0 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🛡️ Read our{" "}
                    <a
                      onClick={() => openBlogModal("our-protection-guarantee")}
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "rgba(0, 0, 0, 0.6)",
                      }}
                    >
                      Buyer Protection Guarantee
                    </a>
                  </Typography>
                </Divider>
                <Grid container spacing={2} justifyContent="space-between">
                  <Grid item xs={6}>
                    <ButtonStyled
                      text="Add to Cart"
                      variant="outlined"
                      color="primary"
                      clickHandler={handleAddToCart}
                      disabled={
                        isProductDeleted || (!!profileId && isOutOfStock)
                      }
                      loading={isAddingToCart}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ButtonStyled
                      text="Make Offer"
                      variant="outlined"
                      color="primary"
                      clickHandler={handleMakeOffer}
                      disabled={
                        isProductDeleted || (!!profileId && isOutOfStock)
                      }
                    />
                  </Grid>
                </Grid>
                <ButtonStyled
                  text="Buy Now"
                  clickHandler={() => {
                    if (profileId) {
                      // If logged in and out of stock, show message
                      if (isOutOfStock) {
                        snackbarRef.current?.showSnackbar(
                          "This item is out of stock",
                          "warning",
                        );
                        return;
                      }
                    } else {
                      setAccountModalOpen(true);
                      setShowSignInAlertMessage(true);
                    }
                  }}
                  disabled={isProductDeleted || (!!profileId && isOutOfStock)}
                />

                <Divider sx={{ mt: 0, mb: 2 }} textAlign="right">
                  <img
                    src="/stripe.png"
                    alt="Powered by Stripe"
                    style={{
                      height: "22px",
                    }}
                  />
                </Divider>
                <Typography variant="body2" color="grey" gutterBottom>
                  Listed on {formatDate(Number(products[0].createdTime))}
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  <b>Item Description</b>
                  <br /> {products[0].productDescriprion}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <b>Shipping Information</b>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <img
                    src="/usa.png"
                    alt="USA"
                    style={{
                      height: "14px",
                      marginLeft: "2px",
                      verticalAlign: "text-bottom",
                    }}
                  />{" "}
                  USA:{" "}
                  <b>
                    {products[0].productUsaShipping === FREE_SHIPPING_PRICE
                      ? "Free Shipping"
                      : products[0].productUsaShipping}
                  </b>
                </Typography>

                <Divider sx={{ mt: 2, mb: 2 }} />

                <Typography variant="body2" gutterBottom>
                  <ClockIcon sx={{ verticalAlign: "middle" }} /> Estimated
                  arrival: <b>{addDaysToCurrentTimeStamp(15)}</b> if you order
                  today.
                  <br />
                  <br />
                  <LocalShippingOutlinedIcon
                    sx={{ verticalAlign: "middle" }}
                  />{" "}
                  Shipping: Includes <b>FREE</b> tracking!
                  <br />
                  <br />
                  <ReturnIcon sx={{ verticalAlign: "middle" }} /> Returns:{" "}
                  {isProductDeleted ? (
                    "Contact Seller"
                  ) : (
                    <Link onClick={handleMailTo} style={{ cursor: "pointer" }}>
                      Contact Seller
                    </Link>
                  )}
                </Typography>

                <Alert
                  severity="warning"
                  variant="outlined"
                  style={{ marginBottom: "0px", borderRadius: "16px" }}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2" sx={{ marginBottom: "10px" }}>
                    <b>ATTENTION:</b> Feel free to ask the seller anything
                    before buying. Conduct transactions only through
                    FiddlePiddle. Expect a tracking number within 24-72 hours.
                    Don't forget to check spam!
                  </Typography>

                  <Chip
                    size="small"
                    label="Contact Seller"
                    icon={<PersonOutlineOutlinedIcon />}
                    onClick={handleMailTo}
                    disabled={isProductDeleted}
                  />
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          {!isDesktop &&
          moreProducts &&
          moreProducts.length > 0 &&
          !isProductDeleted ? (
            <Grid item xs={12} sx={{ padding: 0 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <Typography variant="h6">
                  More listings from {getSellerName(products[0].productOwner)}
                </Typography>
                <ReactRouterLink
                  to={`/u/${sellerInfo.createdTime}/profile`}
                  isLinkUnderline={false}
                  linkElement={
                    <Chip
                      size="small"
                      label="See More"
                      icon={<VisibilityOutlinedIcon />}
                      onClick={() => {}}
                      sx={{ marginLeft: "5px" }}
                    />
                  }
                />
              </span>
              <Grid container spacing={2}>
                {moreProducts.map((product, index) => (
                  <Grid item key={index} xs={6} sm={4} md={3} lg={2}>
                    <ReactRouterLink
                      to={product.productUrl}
                      isLinkUnderline={false}
                      linkElement={
                        <CardActionArea
                          style={{
                            outline: "1px solid #f0f0f0",
                            borderRadius: "8px",
                          }}
                        >
                          <ToyCard product={product} loading={loading} />
                        </CardActionArea>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ) : null}
          {similarProducts && similarProducts.length > 0 ? (
            <Grid item xs={12} sx={{ padding: 0 }}>
              <Typography variant="h6" sx={{ marginBottom: "10px" }}>
                Recommended for you
              </Typography>
              <Grid container spacing={2}>
                {similarProducts.map((product, index) => (
                  <Grid item key={index} xs={6} sm={4} md={3} lg={2}>
                    <ReactRouterLink
                      to={product.productUrl}
                      isLinkUnderline={false}
                      linkElement={
                        <CardActionArea
                          style={{
                            outline: "1px solid #f0f0f0",
                            borderRadius: "8px",
                          }}
                        >
                          <ToyCard product={product} loading={loading} />
                        </CardActionArea>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      </Container>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <SignUpSection />
      </Container>
      <SnackbarAlert ref={snackbarRef} />
      <DrawerModal
        isOpen={isMakeOfferModalOpen}
        onClose={closeMakeOfferModal}
        anchor="bottom"
        onOpen={openMakeOfferModal}
      >
        <ComingSoon />
      </DrawerModal>
      <DrawerModal
        isOpen={isAIAnalysisModalOpen}
        onClose={closeAIAnalysisModal}
        anchor="bottom"
        onOpen={openAIAnalysisModal}
      >
        <ComingSoon />
      </DrawerModal>
      <DrawerModal
        isOpen={isShareModalOpen}
        onClose={closeShareModal}
        onOpen={openShareModal}
        anchor={"bottom"}
      >
        <SocialMediaShare
          shareURL={`${window.location.origin}${products[0].productUrl}`}
          title={products[0].productName}
          closeShareModal={closeShareModal}
        />
      </DrawerModal>
      <DrawerModal
        isOpen={isBlogModalOpen}
        onClose={closeBlogModal}
        anchor="bottom"
        onOpen={() => openBlogModal}
      >
        <div
          style={{
            maxHeight: isDesktop ? "400px" : "auto",
            overflowY: isDesktop ? "auto" : "visible",
          }}
        >
          <Container maxWidth="md">
            {!blogsLoading && blogsData && blogsData?.blogs.length > 0 ? (
              <>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "1.25rem", fontWeight: "bold" }}>
                    {blogsData?.blogs[0].blogTitle}
                  </span>
                  <br />
                  <span style={{ fontSize: "smaller", color: "grey" }}>
                    Last updated {formatDate(blogsData?.blogs[0].createdTime)}
                  </span>
                </div>
                <div
                  style={{ margin: "0 auto", maxWidth: "800px" }}
                  dangerouslySetInnerHTML={{
                    __html: he.decode(blogsData?.blogs[0].blogContent),
                  }}
                />
              </>
            ) : (
              <CenteredLoading />
            )}
          </Container>
        </div>
      </DrawerModal>
      <DrawerModal
        isOpen={isAccountModalOpen}
        onClose={() => {
          setAccountModalOpen(false);
        }}
        anchor="bottom"
        onOpen={() => {}}
      >
        <Login
          openFormFrom={"User"}
          alertMessage={
            showSignInAlertMessage
              ? "You must be signed in to perform that action."
              : ""
          }
          onLogin={() => {
            setAccountModalOpen(false);
            if (getSession(USER_JUST_LOGGED_IN)) {
              snackbarRef.current?.showSnackbar(
                "Signed in successfully",
                "success",
              );
              deleteSession(USER_JUST_LOGGED_IN);
            }
          }}
          isOpenFromOpenOtherPage
        />
      </DrawerModal>
      <DrawerModal
        isOpen={isAlertVerifyUserModalOpen}
        onClose={() => {
          setIsAlertVerifyUserModalOpen(false);
        }}
        anchor="bottom"
        onOpen={() => {}}
      >
        <p>
          You must confirm your account to continue. Look for the confirmation
          email in your inbox, or{" "}
          <Link className="clickableLink" onClick={handleSendConfirmEmail}>
            click here
          </Link>{" "}
          to resend it.
        </p>
      </DrawerModal>
    </>
  );
};

export default ProductView;
